import { PermissionEnum } from '@Application/Security/Roles';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import ArrowLink from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/ArrowLink';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type TranscriptionIssueView from '@UI/Store/WorkflowRuns/Views/TranscriptionIssueView';
import { css } from '@emotion/react';
import type * as React from 'react';

const orientationStyle = css`
	margin: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.375rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

type Orientation = {
	readonly message: string;
	readonly linkLabel: string;
	readonly linkTo: string;
	readonly permission: PermissionEnum;
	readonly fallback: string;
};

const ENGINES_LINK = {
	linkLabel: 'Configurer les moteurs',
	linkTo: '/facilities/ai-assignments',
	permission: PermissionEnum.AI_ASSIGNMENT_READ,
} as const;

const SPEAKER_EXTRACTION_ORIENTATION: Orientation = {
	...ENGINES_LINK,
	message: "Aucun moteur d'extraction des locuteurs n'est affecté à cette agence.",
	fallback: "Contactez votre administrateur pour faire configurer le moteur d'extraction des locuteurs.",
};

/** Every other step type keeps the transcription wording. */
const TRANSCRIPTION_ORIENTATION: Orientation = {
	...ENGINES_LINK,
	message: "Aucun moteur de transcription n'est affecté à cette agence.",
	fallback: 'Contactez votre administrateur pour faire configurer le moteur de transcription.',
};

type Props = {
	readonly issue: TranscriptionIssueView;
};

/** Tells the reader what to configure after a configuration failure; renders nothing for any other cause. */
const ConfigurationOrientation: React.FC<Props> = ({ issue }) => {
	let orientation: Orientation | null = null;

	if (issue.isConfigurationFailure) {
		orientation =
			issue.step.value === 'speaker_separation'
				? SPEAKER_EXTRACTION_ORIENTATION
				: TRANSCRIPTION_ORIENTATION;
	}

	return (
		<ConditionalLoader data={orientation}>
			{(found) => (
				<p css={orientationStyle}>
					{found.message}
					<WithPermission permission={found.permission} fallback={found.fallback}>
						<ArrowLink to={found.linkTo}>{found.linkLabel}</ArrowLink>
					</WithPermission>
				</p>
			)}
		</ConditionalLoader>
	);
};

export default ConfigurationOrientation;
